// internal-deps
import { VALID_PAYMENT_METHODS, isEmpty, VALID_UPI_PAYMENT_CODE } from "@swiggy-private/payment-core";
import { format, SESSION_STORAGE_KEYS } from "@swiggy-private/core-ui";
import { AllUpiIntentAvailableMethodsResponse } from "@swiggy-private/payment-types";
import { Storage } from "@swiggy-private/core-ui";

// interfaces
import {
    PaymentGroupMeta,
    PaymentMethod,
    PaymentMethodMeta,
    PaymentGroup,
    UpiPaymentMethod,
} from "@payment-ui/interfaces";

// connstants
import { BASE_64_IMAGE_PREFIX, TEXTS } from "@payment-ui/constants/index";
import { PAY_VIA, SECS_PER_MINUTE, THIRD_PARTY_UPI_INTENT } from "@payment-ui/constants/Upi";

// helpers
import { getPaymentMethods } from "./Payment";
import { isInAppUpiOnboardingMethod } from "./InAppUpi";

export const isNewUpiPaymentMethod = (methodData: UpiPaymentMethod): boolean => {
    const { name } = methodData;
    return VALID_PAYMENT_METHODS.NEW_UPI === name.toUpperCase();
};

export const getAcceptUpiLogoList = (meta: UpiPaymentMethod["meta"]): UpiPaymentMethod["meta"]["defaultLogos"] => {
    return meta.defaultLogos;
};

export const getPayCtaMsg = (methodData: UpiPaymentMethod, paymentAmount: string): string => {
    return methodData.meta.ctaMsg ?? format(TEXTS.PAY_X, paymentAmount);
};

export const isValidVpa = (newVpaAddress: string): boolean => {
    return !isEmpty(newVpaAddress) && /\w@\w/.test(newVpaAddress);
};

export const getTwoDigitString = (i: number): string => {
    if (i < 10) {
        return `0${i}`;
    } else {
        return i.toString();
    }
};

export const getTimeInMins = (timeLeft: number): string => {
    const minsLeft = Math.floor(timeLeft / SECS_PER_MINUTE);
    const _zeroPadMins = ("0" + minsLeft).slice(-2);
    const secsLeft = getTwoDigitString(timeLeft - minsLeft * SECS_PER_MINUTE);

    return `${_zeroPadMins}:${secsLeft}`;
};

export const isUpiIntentMethod = (method: PaymentMethod<PaymentMethodMeta>): boolean => {
    const paymentCode = method.paymentCode.toUpperCase();
    return paymentCode === VALID_UPI_PAYMENT_CODE.UPI_INTENT || paymentCode === VALID_UPI_PAYMENT_CODE.PHONEPE_UPI;
};

export const isUpiCollectMethod = (method: PaymentMethod<PaymentMethodMeta>): boolean =>
    method.paymentCode.toUpperCase() === VALID_UPI_PAYMENT_CODE.UPI_COLLECT;

export const getUpiPaymentMethodsByType = <T extends PaymentGroupMeta, U extends PaymentMethod<PaymentMethodMeta>>(
    data: PaymentGroup<T, U>,
): { inAppUpiMethod: U[]; upiIntentMethods: U[]; upiCollectMethods: U[] } => {
    const paymentMethods = getPaymentMethods(data);
    return {
        inAppUpiMethod: paymentMethods.filter(isInAppUpiOnboardingMethod),
        upiIntentMethods: paymentMethods.filter(isUpiIntentMethod),
        upiCollectMethods: paymentMethods.filter(isUpiCollectMethod),
    };
};

export const getTurboPayCtaTextProps = ({
    amountWithDiscount,
    amountWithoutDiscount,
    methodData,
}: {
    amountWithDiscount: string;
    amountWithoutDiscount?: string;
    methodData: UpiPaymentMethod;
}) => {
    const isMethodUPI = isUpiCollectMethod(methodData) || isUpiIntentMethod(methodData);

    return {
        amountWithDiscount,
        amountWithoutDiscount: amountWithoutDiscount ?? "",
        mainText: TEXTS.PAY,
        subText:
            isMethodUPI && methodData.meta.ctaMsg?.includes(TEXTS.VIA)
                ? TEXTS.VIA + methodData.meta.ctaMsg.split(TEXTS.VIA)[1]
                : "",
    };
};

export const getUpiPackageNameFromNBResponse = (allUpiIntentAvailableMethods: AllUpiIntentAvailableMethodsResponse) => {
    return allUpiIntentAvailableMethods?.upiAppsDetails?.map(upiApp => upiApp.packageName);
};

export const getAppsNotPresentInPaymentResponse = (
    upiAppsDetailsFromSessionStorage: AllUpiIntentAvailableMethodsResponse,
    upiIntentMethods: UpiPaymentMethod[],
): string[] => {
    // package names of apps installed in user's device
    const allInstalledUpiIntentAppsPackageNames = upiAppsDetailsFromSessionStorage.upiAppsDetails.map(
        upiApp => upiApp.packageName,
    );

    // package names of apps present in GPO response
    const paymentResponseUpiIntentAppsPackageNames = upiIntentMethods.map(upiIntentMethod => upiIntentMethod.name);

    const appsNotPresentInPaymentResponse = allInstalledUpiIntentAppsPackageNames.filter(
        allInstalledAppsPackageName => !paymentResponseUpiIntentAppsPackageNames.includes(allInstalledAppsPackageName),
    );

    return appsNotPresentInPaymentResponse;
};

export const transformUpiIntentMethods = (upiIntentMethods: UpiPaymentMethod[]) => {
    const upiAppsDetailsFromSessionStorage: AllUpiIntentAvailableMethodsResponse | undefined = Storage.getItem(
        SESSION_STORAGE_KEYS.THIRD_PARTY_UPI_INTENT_APPS_DETAILS,
    );

    if (isEmpty(upiIntentMethods) || isEmpty(upiAppsDetailsFromSessionStorage)) {
        return upiIntentMethods;
    }

    const indexOfThirdPartyUpiIntentApp = upiIntentMethods.findIndex(
        upiIntentMethod => upiIntentMethod.name === THIRD_PARTY_UPI_INTENT,
    );

    if (indexOfThirdPartyUpiIntentApp === -1) {
        return upiIntentMethods;
    }

    const appsNotPresentInPaymentResponse = getAppsNotPresentInPaymentResponse(
        upiAppsDetailsFromSessionStorage as AllUpiIntentAvailableMethodsResponse,
        upiIntentMethods,
    );

    // updating iconUrl and name of third party apps from the data we get from new upi NB
    const transformedUpiIntentAppsList = appsNotPresentInPaymentResponse.map(app => {
        const appDetailsFromSessionStorage = (upiAppsDetailsFromSessionStorage as AllUpiIntentAvailableMethodsResponse)?.upiAppsDetails.find(
            upiApp => upiApp?.packageName === app,
        );

        if (!isEmpty(appDetailsFromSessionStorage)) {
            return {
                ...upiIntentMethods[indexOfThirdPartyUpiIntentApp],
                name: appDetailsFromSessionStorage?.packageName,
                upiAppName: appDetailsFromSessionStorage?.displayName,
                displayName: appDetailsFromSessionStorage?.displayName,
                isThirdPartyIntentApp: true,
                meta: {
                    ...upiIntentMethods[indexOfThirdPartyUpiIntentApp]?.meta,
                    iconUrl: appDetailsFromSessionStorage?.iconUrl,
                    ctaMsg: `${PAY_VIA} ${appDetailsFromSessionStorage?.displayName}`,
                },
            } as UpiPaymentMethod;
        }
    });

    const transformedUpiIntentAppsListClone = transformedUpiIntentAppsList as UpiPaymentMethod[];

    upiIntentMethods.splice(indexOfThirdPartyUpiIntentApp, 1);
    upiIntentMethods.push(...transformedUpiIntentAppsListClone);

    return upiIntentMethods;
};

export const upiAppsRenderingLogic = (intentMethods: UpiPaymentMethod[], collectMethods: UpiPaymentMethod[]) => {
    const upiAppsDetailsFromSessionStorage: AllUpiIntentAvailableMethodsResponse | undefined = Storage.getItem(
        SESSION_STORAGE_KEYS.THIRD_PARTY_UPI_INTENT_APPS_DETAILS,
    );

    if (isEmpty(upiAppsDetailsFromSessionStorage)) {
        return [...intentMethods, ...collectMethods];
    }

    const { enabledIntentMethods, disabledIntentMethods } = intentMethods.reduce(
        (acc, intentMethod) => {
            intentMethod.enabled
                ? acc.enabledIntentMethods.push(intentMethod)
                : acc.disabledIntentMethods.push(intentMethod);

            return acc;
        },
        { enabledIntentMethods: [] as UpiPaymentMethod[], disabledIntentMethods: [] as UpiPaymentMethod[] },
    );

    const { enabledCollectMethods, disabledCollectMethods } = collectMethods.reduce(
        (acc, collectMethod) => {
            collectMethod.enabled
                ? acc.enabledCollectMethods.push(collectMethod)
                : acc.disabledCollectMethods.push(collectMethod);

            return acc;
        },
        { enabledCollectMethods: [] as UpiPaymentMethod[], disabledCollectMethods: [] as UpiPaymentMethod[] },
    );

    return [...enabledIntentMethods, ...enabledCollectMethods, ...disabledIntentMethods, ...disabledCollectMethods];
};

export const generatebase64IconUrl = (src: string) => {
    return `${BASE_64_IMAGE_PREFIX}${src}`;
};
